import { useEffect } from "react";
import { useConfigContext } from "./useConfigContext";

/**
 * Hook to detect clicks outside multiple referenced elements, and optionally ESC key presses.
 *
 * @param refs - The refs to the elements to detect outside clicks for
 * @param callback - The function to call when an outside click or ESC is detected
 * @param closeOnEscape - Whether ESC key should also trigger the callback (defaults to true)
 */
export const useOutsideClick = (closeOnEscape = true) => {
  const {
    notificationsRef,
    shareRef,
    settingsRef,
    widgetSwitchRef,
    drawerSwitchRef,
    searchRef,
    dispatch,
    state,
  } = useConfigContext();

  useEffect(() => {
    // Handler for mouse click events to detect clicks outside specified elements
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;

      // Array of refs with their open state and corresponding action types
      const refChecks: {
        ref: React.RefObject<HTMLElement>;
        isOpen: boolean;
        actionType:
          | "SHARE_VISIBILITY"
          | "SETTINGS_VISIBILITY"
          | "WIDGETS_VISIBILITY"
          | "NOTIFICATIONS_VISIBILITY"
          | "DRAWER_VISIBILITY"
          | "SEARCH_VISIBILITY";
      }[] = [
        {
          ref: shareRef as React.RefObject<HTMLElement>,
          isOpen: state.isShareOpen,
          actionType: "SHARE_VISIBILITY",
        },
        {
          ref: settingsRef as React.RefObject<HTMLElement>,
          isOpen: state.isSettingsOpen,
          actionType: "SETTINGS_VISIBILITY",
        },
        {
          ref: widgetSwitchRef as React.RefObject<HTMLElement>,
          isOpen: state.isWidgetsOpen,
          actionType: "WIDGETS_VISIBILITY",
        },
        {
          ref: notificationsRef as React.RefObject<HTMLElement>,
          isOpen: state.isNotificationsOpen,
          actionType: "NOTIFICATIONS_VISIBILITY",
        },
        {
          ref: drawerSwitchRef as React.RefObject<HTMLElement>,
          isOpen: state.isDrawerOpen,
          actionType: "DRAWER_VISIBILITY",
        },
        {
          ref: searchRef as React.RefObject<HTMLElement>,
          isOpen: state.isSearchOpen,
          actionType: "SEARCH_VISIBILITY",
        },
      ];

      // For each ref, check if the click target is outside the element
      refChecks.forEach(({ ref, isOpen, actionType }) => {
        // We check if ref.current exists and whether it contains the click target.
        // If the click is outside and the element is open, we dispatch an action to close it.
        if (isOpen && ref.current && !ref.current.contains(target)) {
          dispatch({ type: actionType, payload: { visible: false } });
        }
      });
    };

    // Handler for keydown events to detect ESC key presses
    const handleKeyDown = (event: KeyboardEvent) => {
      // If ESC is pressed and closeOnEscape is true, close all relevant UI panels
      if (event.key === "Escape" && closeOnEscape) {
        dispatch({ type: "SETTINGS_VISIBILITY", payload: { visible: false } });
        dispatch({ type: "WIDGETS_VISIBILITY", payload: { visible: false } });
        dispatch({ type: "SHARE_VISIBILITY", payload: { visible: false } });
        dispatch({
          type: "NOTIFICATIONS_VISIBILITY",
          payload: { visible: false },
        });
        dispatch({ type: "DRAWER_VISIBILITY", payload: { visible: false } });
        dispatch({ type: "SEARCH_VISIBILITY", payload: { visible: false } });
      }
    };

    // Register event listeners for mouse clicks and keydown events
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup event listeners on unmount or when dependencies change
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    dispatch,
    state,
    closeOnEscape,
    shareRef,
    settingsRef,
    widgetSwitchRef,
    notificationsRef,
    drawerSwitchRef,
    searchRef,
  ]);
};
